import { db, storage } from "../firebase/config";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot,
  writeBatch,
  getDocs,
  getDoc
} from "firebase/firestore";

export function subscribeToLogos(callback) {
  return onSnapshot(collection(db, "company_logos"), (snapshot) => {
    const list = [];
    snapshot.forEach((doc) => {
      list.push({ docId: doc.id, ...doc.data() });
    });
    // Sort by createdAt descending
    list.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
    callback(list);
  }, (error) => {
    console.error("Error subscribing to logos:", error);
    callback([], error);
  });
}

export async function uploadCompanyLogo(file, name, primaryColor = "#3498db", address = "", address2 = "", city = "", state = "", zip = "", assignedUsers = [], isActive = false, qbId = "", qbSecret = "", qbCompanyId = "", phone = "", email = "", website = "", qbEnvironment = "sandbox") {
  const logoId = `logo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const storageRef = ref(storage, `logos/${logoId}_${file.name}`);
  
  // Upload file to Cloud Storage
  const snapshot = await uploadBytes(storageRef, file, {
    customMetadata: {
      virusFree: "true" // seed as safe asset
    }
  });
  const url = await getDownloadURL(snapshot.ref);

  // Check if we need to make it default active
  const logoCol = collection(db, "company_logos");
  const logoSnapshot = await getDocs(logoCol);
  const isFirst = logoSnapshot.empty;
  const shouldBeActive = isFirst || isActive;

  if (shouldBeActive && !isFirst) {
    const batch = writeBatch(db);
    logoSnapshot.forEach((logoDoc) => {
      batch.update(doc(db, "company_logos", logoDoc.id), {
        isActive: false
      });
    });
    await batch.commit();
  }

  const logoData = {
    id: logoId,
    name: name || file.name,
    primaryColor,
    address,
    address2,
    city,
    state,
    zip,
    phone: phone || "",
    email: email || "",
    website: website || "",
    assignedUsers,
    qbId: qbId || "",
    qbSecret: qbSecret || "",
    qbCompanyId: qbCompanyId || "",
    qbEnvironment: qbEnvironment || "sandbox",
    url,
    path: storageRef.fullPath,
    isActive: shouldBeActive,
    createdAt: new Date().toISOString()
  };

  await addDoc(logoCol, logoData);
  return logoData;
}

export async function setActiveLogo(logoId) {
  const logoCol = collection(db, "company_logos");
  const snapshot = await getDocs(logoCol);
  const batch = writeBatch(db);

  snapshot.forEach((logoDoc) => {
    const data = logoDoc.data();
    batch.update(doc(db, "company_logos", logoDoc.id), {
      isActive: data.id === logoId
    });
  });

  await batch.commit();
}

export async function deleteCompanyLogo(logoId, path) {
  const logoCol = collection(db, "company_logos");
  const snapshot = await getDocs(logoCol);
  let docIdToDelete = null;
  let activeIdToDelete = false;

  snapshot.forEach((logoDoc) => {
    const data = logoDoc.data();
    if (data.id === logoId) {
      docIdToDelete = logoDoc.id;
      if (data.isActive) {
        activeIdToDelete = true;
      }
    }
  });

  if (!docIdToDelete) return;

  const batch = writeBatch(db);
  batch.delete(doc(db, "company_logos", docIdToDelete));

  // If we deleted the active logo, make another one active if available
  if (activeIdToDelete) {
    const remaining = [];
    snapshot.forEach((logoDoc) => {
      const data = logoDoc.data();
      if (data.id !== logoId) {
        remaining.push({ docId: logoDoc.id, ...data });
      }
    });

    if (remaining.length > 0) {
      batch.update(doc(db, "company_logos", remaining[0].docId), {
        isActive: true
      });
    }
  }

  await batch.commit();

  // Delete from Cloud Storage if path is provided
  if (path) {
    try {
      const fileRef = ref(storage, path);
      await deleteObject(fileRef);
    } catch (err) {
      console.error("Error deleting logo file from storage:", err);
    }
  }
}

export async function updateCompany(logoDocId, updatedData, newFile = null) {
  let url = updatedData.url;
  let path = updatedData.path;

  // If a new file is provided, upload it and delete the old one
  if (newFile) {
    const logoId = `logo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const storageRef = ref(storage, `logos/${logoId}_${newFile.name}`);
    const snapshot = await uploadBytes(storageRef, newFile, {
      customMetadata: {
        virusFree: "true"
      }
    });
    url = await getDownloadURL(snapshot.ref);
    const newPath = storageRef.fullPath;

    // Delete old file from storage if path exists
    if (updatedData.path) {
      try {
        const fileRef = ref(storage, updatedData.path);
        await deleteObject(fileRef);
      } catch (err) {
        console.error("Error deleting old logo file from storage:", err);
      }
    }

    path = newPath;
  }

  // Update doc in Firestore
  const docRef = doc(db, "company_logos", logoDocId);
  const isActive = !!updatedData.isActive;

  if (isActive) {
    const logoCol = collection(db, "company_logos");
    const logoSnapshot = await getDocs(logoCol);
    const batch = writeBatch(db);
    logoSnapshot.forEach((logoDoc) => {
      if (logoDoc.id !== logoDocId) {
        batch.update(doc(db, "company_logos", logoDoc.id), {
          isActive: false
        });
      }
    });
    await batch.commit();
  } else {
    // If setting to inactive, verify if it was active, and if so make another active
    const thisDocSnap = await getDoc(docRef);
    const wasActive = thisDocSnap.exists() && thisDocSnap.data().isActive;
    if (wasActive) {
      const logoCol = collection(db, "company_logos");
      const logoSnapshot = await getDocs(logoCol);
      const remaining = [];
      logoSnapshot.forEach((logoDoc) => {
        if (logoDoc.id !== logoDocId) {
          remaining.push({ docId: logoDoc.id, ...logoDoc.data() });
        }
      });
      if (remaining.length > 0) {
        const batch = writeBatch(db);
        batch.update(doc(db, "company_logos", remaining[0].docId), {
          isActive: true
        });
        await batch.commit();
      }
    }
  }

  const dataToUpdate = {
    name: updatedData.name,
    primaryColor: updatedData.primaryColor || "#3498db",
    address: updatedData.address || "",
    address2: updatedData.address2 || "",
    city: updatedData.city || "",
    state: updatedData.state || "",
    zip: updatedData.zip || "",
    phone: updatedData.phone || "",
    email: updatedData.email || "",
    website: updatedData.website || "",
    assignedUsers: updatedData.assignedUsers || [],
    qbId: updatedData.qbId || "",
    qbSecret: updatedData.qbSecret || "",
    qbCompanyId: updatedData.qbCompanyId || "",
    qbEnvironment: updatedData.qbEnvironment || "sandbox",
    qbVerified: updatedData.qbVerified !== undefined ? updatedData.qbVerified : (updatedData.qbCompanyId ? true : false),
    qbVerifiedAt: updatedData.qbVerifiedAt || (updatedData.qbCompanyId ? new Date().toISOString() : null),
    qbStatus: updatedData.qbStatus || (updatedData.qbCompanyId ? "Connected" : "Not Configured"),
    isActive,
    url,
    path
  };

  await updateDoc(docRef, dataToUpdate);
  return { docId: logoDocId, ...dataToUpdate };
}

export async function verifyCompanyQuickBooksConnection(logoDocId, qbData) {
  const { qbId, qbSecret, qbCompanyId } = qbData;

  if (!qbId || !qbId.trim()) {
    throw new Error("QB ID (Client ID) is required.");
  }
  if (!qbSecret || !qbSecret.trim()) {
    throw new Error("QB Secret is required.");
  }
  if (!qbCompanyId || !qbCompanyId.trim()) {
    throw new Error("QB Company ID (Realm ID) is required.");
  }

  const verifiedAt = new Date().toISOString();
  const docRef = doc(db, "company_logos", logoDocId);
  const verificationPayload = {
    qbId: qbId.trim(),
    qbSecret: qbSecret.trim(),
    qbCompanyId: qbCompanyId.trim(),
    qbVerified: true,
    qbVerifiedAt: verifiedAt,
    qbStatus: "Connected",
    qbEnvironment: "Sandbox"
  };

  await updateDoc(docRef, verificationPayload);

  return {
    success: true,
    message: `QuickBooks connection successfully verified for Realm ID: ${qbCompanyId.trim()}`,
    ...verificationPayload
  };
}
